import Card from 'components/Card';
import { Typography, Stack, Box, Button, Link } from '@mui/material';
import {
  TextFieldControl,
  CheckboxControl,
  Form,
  MASK,
  useToast,
  CircularLoading,
} from '@myhrbox/myhrbox-frontend-core';
import { useForm } from 'react-hook-form';
import {
  useSetCandidateAgreementTrueMutation,
  useSetCandidatePhoneNumberMutation,
} from '@generated/graphql';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ROUTES from 'constants/routes';
import useMe from 'store/user/useMe';
import { ConfirmDataForm } from './types';

const ConfirmData = () => {
  const { me, loading: meLoading } = useMe();
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const form = useForm<ConfirmDataForm>({
    values: {
      phoneNumber: me?.phoneNumber || '',
      confirmAgreement: me?.agreement || false,
    },
  });
  const { handleSubmit } = form;

  const [saveAgreement, { loading: saveAgreementLoading }] = useSetCandidateAgreementTrueMutation();
  const [savePhoneNumber, { loading: savePhoneNUmberLoading }] =
    useSetCandidatePhoneNumberMutation();

  const onSaveCandidateData = async (formData: ConfirmDataForm) => {
    try {
      if (!me?.candidateUsername.login) {
        const resPhoneNumber = await savePhoneNumber({
          variables: { phoneNumber: formData.phoneNumber },
        });
        const isPhoneNumberSaved = !!resPhoneNumber.data?.setCandidatePhoneNumber.phoneNumber;
        if (!isPhoneNumberSaved) throw new Error(t('languageSelect.errorSavingAgreement'));
      }

      const resAgreement = await saveAgreement();
      const isAgreementSaved = !!resAgreement.data?.setCandidateAgreementTrue.agreement;
      if (!isAgreementSaved) throw new Error(t('languageSelect.errorSavingAgreement'));

      showSuccessToast(t('successMessage'));
      navigate(ROUTES.ASSESSMENT);
    } catch (error: Error | unknown) {
      const errorMessage = error instanceof Error ? error.message : t('errors.common');
      console.error(errorMessage);
      showErrorToast(errorMessage);
    }
  };

  const mutationLoading = saveAgreementLoading || savePhoneNUmberLoading;

  // показываем загрузку, если данные есть, чтобы избежать мелькание формы между окончанием загрузки и редиректом
  if (meLoading || (me?.agreement && me.phoneNumber)) return <CircularLoading />;

  return (
    <Card sx={{ flexGrow: { xs: 1, sm: 0 }, display: 'flex', flexDirection: 'column' }}>
      <Typography variant='h28' mb={{ xs: 1.5, sm: 2 }}>
        {t('confirmUserData.greeting')}
      </Typography>
      <Typography variant='subtitle18rg' mb={4} component='p'>
        {t('confirmUserData.instructions')}
      </Typography>
      <Form form={form}>
        <Stack spacing={3} mb={3}>
          <TextFieldControl
            rules={{ required: !me?.candidateUsername.login }}
            name='phoneNumber'
            label={t('phoneNumber')}
            mask={MASK.PHONE}
          />
          <CheckboxControl
            rules={{ required: t('confirmUserData.confirmPermission') }}
            name='confirmAgreement'
            label={
              <>
                {t('confirmUserData.agreement.1')}{' '}
                <Link
                  href='https://docs.google.com/document/d/1nltBSYJig6bLmnSN6KmiVSDrRmfJl4bCKwfIBKlF7c0/edit?tab=t.0'
                  target='_blank'>
                  {t('confirmUserData.agreement.2')}
                </Link>{' '}
                {t('confirmUserData.agreement.3')}{' '}
                <Link
                  href='https://docs.google.com/document/d/1Q0ygJ1UmfkbXHLW5vX2tI6Rkx8Jv0YY8blyqguw5d9E/edit?tab=t.0'
                  target='_blank'>
                  {t('confirmUserData.agreement.4')}
                  {t('confirmUserData.agreement.5')}
                </Link>
              </>
            }
            disabled={!!me?.agreement}
          />
        </Stack>
      </Form>
      <Box flex={{ xs: 1, sm: 0 }} />
      <Button
        onClick={handleSubmit(onSaveCandidateData)}
        loading={mutationLoading}
        variant='contained'
        size='large'
        fullWidth>
        {t('login')}
      </Button>
    </Card>
  );
};

export default ConfirmData;
